import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Paperclip, ShieldAlert, Loader2, EyeOff } from 'lucide-react';
import { sanitizeMailHtml, looksLikeHtml } from '@/lib/mail';

function fmt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return ''; }
}

/**
 * SaMailPopup — a blocking, must-acknowledge popup for mail sent by a Super
 * Admin. Shows one message at a time (queue) with the FULL subject + body +
 * attachment indicator. It cannot be dismissed except via the "Got it" button,
 * which acknowledges the message (marks it read on the server). Disappearing
 * mails then vanish from the recipient's inbox.
 *
 * Props:
 *   popups: array of pending popup messages (from GET /notifications/sa-popups)
 *   onAck(messageId): acknowledge handler (marks read + removes from queue)
 */
export default function SaMailPopup({ popups = [], onAck }) {
  const [acking, setAcking] = useState(false);
  const current = popups[0] || null;
  const open = !!current;
  const attachments = (current && current.attachments) || [];

  const handleAck = async () => {
    if (!current || acking) return;
    setAcking(true);
    try {
      await onAck?.(current.message_id);
    } finally {
      setAcking(false);
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent
        className="max-w-lg [&>button]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="sa-mail-popup"
      >
        {current && (
          <>
            <DialogHeader className="space-y-0">
              <div className="flex items-center gap-2 text-left">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 shrink-0">
                  <ShieldAlert size={16} />
                </span>
                <div className="min-w-0">
                  <DialogTitle className="text-[15px] leading-tight truncate" data-testid="sa-mail-popup-sender">
                    Message from {current.sender_name || 'Super Admin'}
                  </DialogTitle>
                  <DialogDescription className="text-[11.5px] mt-0.5">
                    Super Admin · {fmt(current.created_at)}
                    {popups.length > 1 ? ` · 1 of ${popups.length}` : ''}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-[15px] font-semibold text-[var(--ink-navy)] break-words" data-testid="sa-mail-popup-subject">
                  {current.subject}
                </div>
                {current.priority && (
                  <Badge className="bg-rose-600 text-white border-none text-[10px]">Priority</Badge>
                )}
                {current.disappearing && (
                  <Badge variant="outline" className="text-[10px] border-amber-300 bg-amber-50 text-amber-700">
                    <EyeOff size={10} className="mr-0.5" /> Viewable once
                  </Badge>
                )}
              </div>

              {looksLikeHtml(current.body) ? (
                <div
                  className="max-h-[46vh] overflow-y-auto rounded-md border border-[var(--divider)] bg-[var(--paper-warm)] p-3 text-[13px] text-slate-700 break-words mail-html-body"
                  data-testid="sa-mail-popup-body"
                  dangerouslySetInnerHTML={{ __html: sanitizeMailHtml(current.body) }}
                />
              ) : (
                <div
                  className="max-h-[46vh] overflow-y-auto rounded-md border border-[var(--divider)] bg-[var(--paper-warm)] p-3 text-[13px] text-slate-700 whitespace-pre-wrap break-words"
                  data-testid="sa-mail-popup-body"
                >
                  {current.body || <span className="text-slate-400 italic">(no message body)</span>}
                </div>
              )}

              {current.has_attachments && attachments.length > 0 && (
                <div className="rounded-md border border-[var(--divider)] p-2">
                  <div className="text-[10.5px] font-medium text-slate-500 mb-1 flex items-center gap-1">
                    <Paperclip size={11} /> {attachments.length} attachment{attachments.length === 1 ? '' : 's'}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {attachments.map((a) => (
                      <span
                        key={a.id}
                        className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600"
                        title={a.name}
                      >
                        <Paperclip size={10} /> <span className="truncate max-w-[180px]">{a.name}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {current.disappearing && (
                <div className="text-[11px] text-amber-600 flex items-center gap-1">
                  <EyeOff size={11} /> This message will disappear from your inbox once you acknowledge it.
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                onClick={handleAck}
                disabled={acking}
                data-testid="sa-mail-popup-ack"
                className="w-full sm:w-auto"
              >
                {acking ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}
                Got it
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
